import { useState } from "react";

import { Modal } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useBostaConnectionQuery } from "@/modules/settings/services/useBostaConnection";
import { useJntConnectionQuery } from "@/modules/settings/services/useJntConnection";
import { BostaDispatchSheet } from "@/modules/orders/components/BostaDispatchSheet";
import { JntDispatchSheet } from "@/modules/orders/components/JntDispatchSheet";

type CarrierId = "bosta" | "jnt";

type Props = {
  storeSlug: string;
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Runs the manual "mark shipped" transition (no carrier label created). */
  onShipManually: () => Promise<void>;
  isShippingManually: boolean;
};

/**
 * "Ship" chooser for a confirmed/preparing order: pick an active carrier to
 * dispatch through, or ship manually (mark shipped). Replaces the old
 * single "Ship with Bosta" CTA now that multiple carriers can be connected.
 */
export function ShipOrderSheet({
  storeSlug,
  orderId,
  isOpen,
  onClose,
  onShipManually,
  isShippingManually,
}: Props) {
  const t = useTranslations();
  const bosta = useBostaConnectionQuery({ storeSlug });
  const jnt = useJntConnectionQuery({ storeSlug });
  const [openCarrier, setOpenCarrier] = useState<CarrierId | null>(null);

  const carriers: { id: CarrierId; name: string }[] = [];
  if (bosta.platformEnabled && bosta.connection?.isActive) {
    carriers.push({ id: "bosta", name: t(keys.bosta.brandName) });
  }
  if (jnt.platformEnabled && jnt.connection?.isActive) {
    carriers.push({ id: "jnt", name: t(keys.jnt.brandName) });
  }

  const handleManual = async () => {
    await onShipManually();
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title={t(keys.shipment.chooseTitle)}
        closeLabel={t(keys.common.close)}
        hideFooter
      >
        <div className="mx-auto flex w-full max-w-[460px] flex-col gap-4">
          <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
            {t(keys.shipment.chooseIntro)}
          </p>

          {carriers.length === 0 ? (
            <p className="rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3 py-2.5 text-[12.5px] text-[color:var(--text-muted)]">
              {t(keys.shipment.noCarriers)}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {carriers.map((carrier) => (
                <ChoiceButton
                  key={carrier.id}
                  title={t(keys.shipment.shipViaCarrier, {
                    carrier: carrier.name,
                  })}
                  onClick={() => setOpenCarrier(carrier.id)}
                />
              ))}
            </div>
          )}

          <div className="border-t border-[color:var(--border-light)] pt-3">
            <ChoiceButton
              title={t(keys.shipment.shipManualTitle)}
              body={t(keys.shipment.shipManualBody)}
              disabled={isShippingManually}
              onClick={() => void handleManual()}
            />
          </div>
        </div>
      </Modal>

      {openCarrier === "bosta" ? (
        <BostaDispatchSheet
          storeSlug={storeSlug}
          orderId={orderId}
          isOpen
          onClose={() => {
            setOpenCarrier(null);
            onClose();
          }}
        />
      ) : null}

      {openCarrier === "jnt" ? (
        <JntDispatchSheet
          storeSlug={storeSlug}
          orderId={orderId}
          isOpen
          onClose={() => {
            setOpenCarrier(null);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

function ChoiceButton({
  title,
  body,
  disabled,
  onClick,
}: {
  title: string;
  body?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full flex-col items-start gap-0.5 rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-start transition hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-alt)] disabled:opacity-60"
    >
      <span className="text-[14px] font-semibold text-[color:var(--text-primary)]">
        {title}
      </span>
      {body ? (
        <span className="text-[12px] leading-relaxed text-[color:var(--text-muted)]">
          {body}
        </span>
      ) : null}
    </button>
  );
}
